import {
    ArrowTopRightOnSquareIcon,
    EnvelopeIcon,
    PaperAirplaneIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import {
    CalendarDate,
    CustomListItemId,
    CustomListKey,
    HomeCareLeadId,
    HomeCarePartnerIntegration,
    LayoutSectionParentKey,
    PhoneNumberE164,
    QuestionSource,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { DateOfBirth } from "components/age-birthday/date-of-birth";
import { InputFormBlock } from "components/app/input-form-block";
import { NgByline } from "components/byline/ng-byline";
import { ClientTransferBanner } from "components/clients/transfers/client-transfer-banner";
import { TransferClientPanel } from "components/clients/transfers/transfer-client-panel";
import { contactTransferLocation } from "components/clients/transfers/transfer-types";
import { Activity } from "components/entity-activity/entity-activity";
import { FilesControl } from "components/files";
import { SignatureRequestPanel } from "components/files/signature-request/signature-request-panel";
import { HomeCareLeadContacts } from "components/home-care-leads/home-care-lead-contacts";
import { HomeCareLeadIntakeTab } from "components/home-care-leads/home-care-lead-intake-tab";
import { HomeCareLeadReferralSource } from "components/home-care-leads/home-care-lead-referral-source";
import { SendToPartnerPanel } from "components/home-care-leads/send-to-partner/send-to-partner-panel";
import { EntityPageHeader } from "components/layout";
import { LayoutFormSection } from "components/layout/layout-form-section";
import { EntityConversationFeed } from "components/messages/entity-conversation-feed";
import { MessageCompose } from "components/messages/message-compose";
import { MessagesTabHeader } from "components/messages/messages-tab-header";
import { Notes } from "components/notes";
import { ImportantNoteBanner } from "components/notes/important-note-banner";
import { ActionBarComponent } from "components/shared/action-bar-component";
import { CustomListSelect } from "components/shared/custom-list-select";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { DuplicateRecordBanner } from "components/shared/duplicate-record-banner";
import { Col, NgGrid, Row } from "components/shared/grid";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { Subnav } from "components/shared/subnav";
import { SubnavItem } from "components/shared/subnav/subnav-types";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { EntityTasks, TaskBadge } from "components/tasks/child";
import { TasksTabLabel } from "components/tasks/tasks-tab-label";
import {
    ActionsMenu,
    ActionsMenuAction,
    ActionsMenuHeader,
    ActionsMenuSection,
    SignWiseMenuAction,
    TransferClientMenuAction,
} from "components/ui/actions-menu";
import { AutosavingInput } from "components/ui/autosaving-input";
import { AutosavingStateInput } from "components/ui/autosaving-state-input";
import { CalendarDateInput } from "components/ui/calendar-date";
import { ContactNameInput } from "components/ui/contact-name-input";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { WorkflowEntityExecutions } from "components/workflow/workflow-entity-executions";
import {
    AdvancedSearchEntityType,
    FileEntityType,
    HomeCareLeadContactRelationship,
    useGetLayoutContainerByKeyQuery,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useCustomList } from "store/selectors/bridge";
import { useFeature, useNow, useUsers } from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { stableQueryOptions, stableQueryOptionsRpc } from "util/requests";
import util from "../../util";
import { LocaleContext } from "../locale-provider/locale-provider";
import { trpc, trpcClient } from "../rpc/trpc";

type HomeCareLeadDetail = inferProcedureOutput<AppRouter["homeCareLead"]["get"]>;

const HomeCareLeadStatusSelector: React.FC<{
    client: HomeCareLeadDetail;
    onStatusChange: (newStatusId: CustomListItemId) => void;
}> = ({ client, onStatusChange }) => {
    const statusName = useStatusName(client.statusListItemId);

    return (
        <ActionBarComponent label="Status" value={statusName}>
            {({ close }) => (
                <CustomListSelect
                    label="Status"
                    customListKey={CustomListKey.HomeCareLeadStatus}
                    onChange={newStatus => {
                        close();
                        onStatusChange(newStatus as CustomListItemId);
                    }}
                    value={client.statusListItemId}
                    valueMode="id"
                />
            )}
        </ActionBarComponent>
    );
};

function useStatusName(statusListItemId: CustomListItemId): string {
    const { customList } = useCustomList(CustomListKey.HomeCareLeadStatus);
    return useMemo(() => {
        return (
            customList?.items.find(
                (x: { id: CustomListItemId; name: string }) => x.id === statusListItemId,
            )?.name || ""
        );
    }, [customList, statusListItemId]);
}

const HomeCareLeadActionBarDate: React.FC<{
    label: string;
    value: string | null;
    onChange: (newVal: string | null) => Promise<string | null>;
    disabled?: boolean;
    disabledReason?: string;
    minDate?: string | null;
    maxDate?: string | null;
}> = ({ label, value, onChange, disabled, disabledReason, minDate, maxDate }) => {
    const display = value ? util.date.formatLongDateNoTime(value) : "(none)";
    const parsed = value ? CalendarDate.parse(value) : null;
    const initial = parsed && parsed.hasValue ? parsed.get() : null;
    const parsedMinDate = minDate ? CalendarDate.parse(minDate) : null;
    const parsedMaxDate = maxDate ? CalendarDate.parse(maxDate) : null;
    const resolvedMinDate =
        parsedMinDate && parsedMinDate.hasValue ? parsedMinDate.get() : null;
    const resolvedMaxDate =
        parsedMaxDate && parsedMaxDate.hasValue ? parsedMaxDate.get() : null;
    const [error, setError] = React.useState<string | null>(null);

    const isOutOfRange = (d: CalendarDate): boolean => {
        if (resolvedMinDate && d.lessThan(resolvedMinDate)) {
            return true;
        }
        if (resolvedMaxDate && resolvedMaxDate.lessThan(d)) {
            return true;
        }
        return false;
    };

    const commit = async (newVal: string | null, close: () => void): Promise<void> => {
        const rejectionReason = await onChange(newVal);
        if (rejectionReason) {
            setError(rejectionReason);
            return;
        }
        setError(null);
        close();
    };

    return (
        <ActionBarComponent
            label={label}
            value={display}
            disabled={disabled}
            disabledReason={disabledReason}>
            {({ close }) => (
                <div className="space-y-1">
                    <CalendarDateInput
                        label={label}
                        initial={initial}
                        minDate={resolvedMinDate}
                        maxDate={resolvedMaxDate}
                        onChange={() => setError(null)}
                        onCommit={d => {
                            if (d && isOutOfRange(d)) {
                                return;
                            }
                            void commit(d ? d.toString() : null, close);
                        }}
                    />
                    {error ? <p className="text-sm text-red-600">{error}</p> : null}
                    <SecondaryButton
                        onClick={() => {
                            void commit(null, close);
                        }}>
                        Clear
                    </SecondaryButton>
                </div>
            )}
        </ActionBarComponent>
    );
};

const HomeCareLeadAssignedTo: React.FC<{
    client: HomeCareLeadDetail;
    onAssignedUserChange: (userId: SelectableUser) => void;
}> = ({ client, onAssignedUserChange }) => {
    const userState = useUsers();

    let assignedUserName = "Unassigned";
    if (client.assignedUserId && userState.users[client.assignedUserId]) {
        assignedUserName = userState.users[client.assignedUserId].name;
    }

    return (
        <ActionBarComponent label="Assigned to" value={assignedUserName}>
            {({ close }) => (
                <UserSelect
                    label="Assigned to"
                    includeEveryone={false}
                    includeUnassigned={true}
                    unassignedLabel="(unassigned)"
                    value={client.assignedUserId || ""}
                    onChange={userId => {
                        close();
                        onAssignedUserChange(userId);
                    }}
                />
            )}
        </ActionBarComponent>
    );
};

const ContactFields: React.FC<{
    contact: HomeCareLeadDetail["selfContactEntity"];
    onFieldChange: (field: string, value: string) => void;
    entityId: HomeCareLeadId;
}> = ({ contact, onFieldChange, entityId }) => {
    return (
        <div className="space-y-2 md:space-y-4">
            <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                <ContactNameInput
                    name={contact.name || ""}
                    preferredName={contact.preferredName || ""}
                    onCommit={(fieldName, newValue) => onFieldChange(fieldName, newValue)}
                />
            </div>
            <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                <PhoneInputE164
                    label="Cell phone number"
                    initial={contact.cellPhone as PhoneNumberE164}
                    onCommit={newVal => onFieldChange("cellPhone", newVal)}
                    entityId={entityId}
                />
                <PhoneInputE164
                    label="Phone number (other)"
                    initial={contact.phone1 as PhoneNumberE164}
                    onCommit={newVal => onFieldChange("phone1", newVal)}
                    entityId={entityId}
                />
            </div>
            <div>
                <AutosavingInput
                    label="Email"
                    initial={contact.email1 || ""}
                    onCommit={newVal => onFieldChange("email1", newVal)}
                />
            </div>
            <div className="space-y-1 md:space-y-2">
                <div>
                    <AutosavingInput
                        label="Address"
                        initial={contact.address1 || ""}
                        onCommit={newVal => onFieldChange("address1", newVal)}
                    />
                </div>
                <LocaleContext.Consumer>
                    {locale => (
                        <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                            <AutosavingInput
                                label="City"
                                initial={contact.city || ""}
                                onCommit={newVal => onFieldChange("city", newVal)}
                            />
                            <AutosavingStateInput
                                label={locale.strings.state}
                                initial={contact.state || ""}
                                onCommit={newVal => onFieldChange("state", newVal)}
                            />
                            <AutosavingInput
                                label={locale.strings.zip}
                                initial={contact.zip || ""}
                                onCommit={newVal => onFieldChange("zip", newVal)}
                            />
                        </div>
                    )}
                </LocaleContext.Consumer>
            </div>
        </div>
    );
};

interface ContactBirthdayPatch {
    birthday: string | null;
    isBirthdayFake: boolean;
}

const ContactDateOfBirth: React.FC<{
    contact: HomeCareLeadDetail["selfContactEntity"];
    onCommit: (patch: ContactBirthdayPatch) => void;
}> = ({ contact, onCommit }) => {
    const now = useNow();
    const parsedBirthday = contact.birthday ? CalendarDate.parse(contact.birthday) : null;
    const birthday =
        parsedBirthday && parsedBirthday.hasValue ? parsedBirthday.get() : null;
    const updateAge = useCallback(
        (ageStr: string) => {
            let parsedAge: number | null = parseInt(ageStr, 10);
            if (isNaN(parsedAge) || parsedAge < 0 || parsedAge > 300) {
                parsedAge = null;
            }
            const newBirthday = parsedAge
                ? CalendarDate.fromLocalDate(
                      util.date.yearsAgo(now, parsedAge),
                  ).toString()
                : null;
            onCommit({ birthday: newBirthday, isBirthdayFake: true });
        },
        [now, onCommit],
    );
    const updateBirthday = useCallback(
        (newBirthday: string) => {
            onCommit({ birthday: newBirthday || null, isBirthdayFake: false });
        },
        [onCommit],
    );
    return (
        <DateOfBirth
            birthday={birthday}
            isBirthdayFake={contact.isBirthdayFake ?? undefined}
            updateAge={updateAge}
            updateBirthday={updateBirthday}
        />
    );
};

const HomeCareLeadOverview: React.FC<{
    client: HomeCareLeadDetail;
    onSelfContactFieldChange: (field: string, value: string) => void;
    onSelfContactBirthdayChange: (patch: ContactBirthdayPatch) => void;
}> = ({ client, onSelfContactFieldChange, onSelfContactBirthdayChange }) => {
    return (
        <InputFormBlock>
            <div className="space-y-2 lg:space-y-4">
                <h3 className="text-lg text-gray-700">Client Info</h3>
                <DuplicateRecordBanner
                    recordType="homeCareLead"
                    recordId={client.id}
                    nameKey={client.selfContactEntity.name ?? ""}
                />
                <ContactFields
                    contact={client.selfContactEntity}
                    onFieldChange={onSelfContactFieldChange}
                    entityId={client.id}
                />
                <ContactDateOfBirth
                    contact={client.selfContactEntity}
                    onCommit={onSelfContactBirthdayChange}
                />
            </div>
        </InputFormBlock>
    );
};

const HomeCareLeadBestContact: React.FC<{
    client: HomeCareLeadDetail;
    onBestContactFieldChange: (field: string, value: string) => void;
}> = ({ client, onBestContactFieldChange }) => {
    return (
        <>
            <InputFormBlock>
                <div className="space-y-2 lg:space-y-4">
                    <h3 className="text-lg text-gray-700">Best Contact Person</h3>
                    <DuplicateRecordBanner
                        recordType="homeCareLead"
                        recordId={client.id}
                        nameKey={client.bestContactEntity.name ?? ""}
                    />
                    <ContactFields
                        contact={client.bestContactEntity}
                        onFieldChange={onBestContactFieldChange}
                        entityId={client.id}
                    />
                </div>
            </InputFormBlock>
            <InputFormBlock>
                <HomeCareLeadContacts
                    homeCareLeadId={client.id}
                    relationship={HomeCareLeadContactRelationship.MoreContact}
                />
            </InputFormBlock>
            <InputFormBlock>
                <HomeCareLeadContacts
                    homeCareLeadId={client.id}
                    relationship={HomeCareLeadContactRelationship.CareProvider}
                />
            </InputFormBlock>
        </>
    );
};

const HomeCareLeadCustomFields: React.FC<{
    client: HomeCareLeadDetail;
    onAnswerCommitted: () => void;
}> = ({ client, onAnswerCommitted }) => {
    const getLayoutContainer = useGetLayoutContainerByKeyQuery(
        { key: LayoutSectionParentKey.HomeCareLeadDetails },
        stableQueryOptions(),
    );

    const sections =
        getLayoutContainer.data?.getLayoutContainerByKey?.layoutSections ?? [];

    if (sections.length === 0) {
        return null;
    }

    return (
        <>
            {sections.map(section => (
                <LayoutFormSection
                    key={section.id}
                    entityId={client.id}
                    entity={client}
                    entityType="HomeCareLead"
                    layoutSection={section}
                    mode="tile"
                    answerMode={QuestionSource.Entity}
                    onAnswerCommitted={onAnswerCommitted}
                />
            ))}
        </>
    );
};

export const ShowHomeCareLead: React.FC = () => {
    const routeMatch = useRouteMatch<{ id: string; subpage?: string }>();
    const homeCareLeadId = routeMatch.params.id as HomeCareLeadId;
    const subpage = routeMatch.params.subpage;
    const isEnabled = useFeature("homeCarePrototype");
    const isWellSkyEnabled = useFeature("wellSkyIntegration");
    const { push } = useHistory();
    const queryClient = useQueryClient();
    const trpcContext = trpc.useTRPC();

    const queryOptions = trpcContext.homeCareLead.get.queryOptions({
        id: homeCareLeadId,
    });
    const { data: client, isLoading } = useQuery(queryOptions);

    const wellSkyHasCredentialQuery = useQuery(
        trpcContext.wellSkyPersonalCare.hasCredential.queryOptions(undefined, {
            enabled: isWellSkyEnabled,
        }),
    );
    const canSendToWellSky =
        isWellSkyEnabled && (wellSkyHasCredentialQuery.data?.hasCredential ?? false);

    const axisCareHasCredentialQuery = useQuery({
        ...trpcContext.axisCare.hasCredential.queryOptions(),
        ...stableQueryOptionsRpc(),
        enabled: isEnabled,
    });
    const canSendToAxisCare =
        isEnabled && (axisCareHasCredentialQuery.data?.hasCredential ?? false);

    const invalidateQuery = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
    }, [queryClient, queryOptions.queryKey]);

    const handleStatusChange = useCallback(
        async (newStatusId: CustomListItemId) => {
            await trpcClient.homeCareLead.update.mutate({
                id: homeCareLeadId,
                statusListItemId: newStatusId,
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleSelfContactFieldChange = useCallback(
        async (field: string, value: string) => {
            await trpcClient.homeCareLead.updateContact.mutate({
                id: homeCareLeadId,
                contactType: "self",
                contactEntity: { [field]: value },
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleSelfContactBirthdayChange = useCallback(
        async (patch: ContactBirthdayPatch) => {
            await trpcClient.homeCareLead.updateContact.mutate({
                id: homeCareLeadId,
                contactType: "self",
                contactEntity: patch,
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleBestContactFieldChange = useCallback(
        async (field: string, value: string) => {
            await trpcClient.homeCareLead.updateContact.mutate({
                id: homeCareLeadId,
                contactType: "bestContact",
                contactEntity: { [field]: value },
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleAssignedUserChange = useCallback(
        async (userId: SelectableUser) => {
            await trpcClient.homeCareLead.update.mutate({
                id: homeCareLeadId,
                assignedUserId: (userId || null) as UserId | null,
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleEstimatedStartDateChange = useCallback(
        async (newVal: string | null): Promise<string | null> => {
            try {
                await trpcClient.homeCareLead.update.mutate({
                    id: homeCareLeadId,
                    estimatedStartDate: newVal,
                });
            } catch (error) {
                return error instanceof Error
                    ? error.message
                    : "Unable to update the estimated start date.";
            }
            invalidateQuery();
            return null;
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleActualStartDateChange = useCallback(
        async (newVal: string | null): Promise<string | null> => {
            if (newVal === null && client.endOfCareDate) {
                const confirmed = await fancyConfirm(
                    "Clear start of care?",
                    "This client has an End of Care date set. Clearing Start of Care will also clear End of Care.",
                    "Yes, clear both",
                    "Cancel",
                );
                if (!confirmed) return null;
            }
            try {
                await trpcClient.homeCareLead.update.mutate({
                    id: homeCareLeadId,
                    actualStartDate: newVal,
                });
            } catch (error) {
                return error instanceof Error
                    ? error.message
                    : "Unable to update the start of care date.";
            }
            invalidateQuery();
            return null;
        },
        [client, homeCareLeadId, invalidateQuery],
    );

    const handleEndOfCareDateChange = useCallback(
        async (newVal: string | null): Promise<string | null> => {
            try {
                await trpcClient.homeCareLead.update.mutate({
                    id: homeCareLeadId,
                    endOfCareDate: newVal,
                });
            } catch (error) {
                return error instanceof Error
                    ? error.message
                    : "Unable to update the end of care date.";
            }
            invalidateQuery();
            return null;
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleDelete = useCallback(async () => {
        if (!client) return;
        const displayName = client.selfContactEntity.name || "this home care client";
        const shouldDelete = await fancyConfirm(
            `Delete ${displayName}?`,
            `Really delete ${displayName}?`,
            "Yes, delete",
            "Cancel",
            { extraConfirm: true },
        );
        if (shouldDelete) {
            await trpcClient.homeCareLead.delete.mutate({
                id: homeCareLeadId,
            });
            push("/home-care-pipeline");
        }
    }, [client, homeCareLeadId, push]);

    const handleNavigate = useCallback(
        (path: string) => {
            push(`/home-care-clients/show/${homeCareLeadId}/${path}`);
        },
        [homeCareLeadId, push],
    );

    const handleCloseSection = useCallback(() => {
        push(`/home-care-clients/show/${homeCareLeadId}`);
    }, [homeCareLeadId, push]);

    const [composing, setComposing] = useState(false);
    const [requestingSignature, setRequestingSignature] = useState(false);
    const [sendingToPartner, setSendingToPartner] =
        useState<HomeCarePartnerIntegration | null>(null);
    const [transferring, setTransferring] = useState(false);
    const files = useSelector(
        (state: ApplicationState) =>
            (state.files.files && state.files.files[homeCareLeadId]) || [],
    );
    const handleCompose = useCallback(() => {
        setComposing(true);
    }, [setComposing]);

    const handleComposeDismiss = useCallback(() => {
        setComposing(false);
    }, [setComposing]);

    const handleRequestSignature = useCallback(() => {
        setRequestingSignature(true);
    }, []);

    const handleCloseSignature = useCallback(() => {
        setRequestingSignature(false);
    }, []);

    const handleCloseSendToPartner = useCallback(() => {
        setSendingToPartner(null);
    }, []);

    const handleTransfer = useCallback(() => {
        setTransferring(true);
    }, []);

    const handleCloseTransfer = useCallback(() => {
        setTransferring(false);
    }, []);

    const renderOverview = useCallback(
        () =>
            client ? (
                <NgGrid>
                    <NgGrid.Column>
                        <HomeCareLeadOverview
                            client={client}
                            onSelfContactFieldChange={handleSelfContactFieldChange}
                            onSelfContactBirthdayChange={handleSelfContactBirthdayChange}
                        />
                        <HomeCareLeadBestContact
                            client={client}
                            onBestContactFieldChange={handleBestContactFieldChange}
                        />
                    </NgGrid.Column>
                    <NgGrid.Column>
                        <Notes entityId={homeCareLeadId} entityType="HomeCareLead" />
                        <HomeCareLeadReferralSource
                            homeCareLead={client}
                            invalidate={invalidateQuery}
                        />
                        <HomeCareLeadCustomFields
                            client={client}
                            onAnswerCommitted={invalidateQuery}
                        />
                        <WorkflowEntityExecutions entity={client} />
                    </NgGrid.Column>
                </NgGrid>
            ) : null,
        [
            client,
            handleSelfContactFieldChange,
            handleSelfContactBirthdayChange,
            handleBestContactFieldChange,
            homeCareLeadId,
            invalidateQuery,
        ],
    );

    const renderIntake = useCallback(
        () =>
            client ? (
                <HomeCareLeadIntakeTab
                    client={client}
                    onAnswerCommitted={invalidateQuery}
                />
            ) : null,
        [client, invalidateQuery],
    );

    const renderMessages = useCallback(() => {
        return (
            <EntityConversationFeed
                entityId={homeCareLeadId}
                entityType={AdvancedSearchEntityType.HomeCareLead}
            />
        );
    }, [homeCareLeadId]);

    const renderFiles = useCallback(() => {
        return (
            <FilesControl
                entityId={homeCareLeadId}
                entityType={FileEntityType.HomeCareLead}
            />
        );
    }, [homeCareLeadId]);

    const renderActivity = useCallback(() => {
        return <Activity entityType="HomeCareLead" entityId={homeCareLeadId} />;
    }, [homeCareLeadId]);

    const renderTasks = useCallback(() => {
        return (
            <Row type="flex" className="collapsible">
                <Col className="full-width">
                    <EntityTasks
                        parentId={homeCareLeadId}
                        defaultAssigneeId={client?.assignedUserId || undefined}
                        entityInfo={{
                            displayName: client?.selfContactEntity?.name || "(no name)",
                            path: `/home-care-clients/show/${homeCareLeadId}`,
                            kind: "HomeCareLead",
                        }}
                    />
                </Col>
            </Row>
        );
    }, [homeCareLeadId, client]);

    const subpages = useMemo(
        () => [
            {
                href: `/home-care-clients/show/${homeCareLeadId}/overview`,
                linkText: "Overview",
                subpage: "overview",
                render: renderOverview,
            },
            {
                href: `/home-care-clients/show/${homeCareLeadId}/intake`,
                linkText: "Intake",
                subpage: "intake",
                render: renderIntake,
            },
            {
                href: `/home-care-clients/show/${homeCareLeadId}/emails`,
                linkText: <MessagesTabHeader />,
                subpage: "emails",
                render: renderMessages,
            },
            {
                href: `/home-care-clients/show/${homeCareLeadId}/tasks`,
                linkText: <TasksTabLabel />,
                subpage: "tasks",
                render: renderTasks,
                badge: <TaskBadge parentId={homeCareLeadId} />,
            } as SubnavItem,
            {
                href: `/home-care-clients/show/${homeCareLeadId}/files`,
                linkText: "Files",
                subpage: "files",
                badge: (files.length || undefined) && {
                    count: files.length,
                    type: "info" as const,
                },
                render: renderFiles,
            },
            {
                href: `/home-care-clients/show/${homeCareLeadId}/history`,
                linkText: "History",
                subpage: "history",
                render: renderActivity,
            },
        ],
        [
            homeCareLeadId,
            renderOverview,
            renderIntake,
            renderMessages,
            renderTasks,
            renderFiles,
            files.length,
            renderActivity,
        ],
    );

    if (!isEnabled) {
        return null;
    }

    if (isLoading || !client) {
        return (
            <div className="flex items-center justify-center p-8">
                <Spinner />
            </div>
        );
    }

    const displayName = client.selfContactEntity.name || "(no name)";
    const bylineEntity = {
        dateAdded: new Date(client.dateAdded),
        updatedAt: new Date(client.updatedAt),
        createdBy: client.createdBy,
        updatedBy: client.updatedBy,
    };

    return (
        <div>
            <EntityPageHeader>
                <div className="flex-1">
                    <h2 className="text-xl font-semibold text-zinc-950">{displayName}</h2>
                    <NgByline entity={bylineEntity} mode="header" />
                </div>
                <div className="flex items-center gap-2">
                    <ActionsMenu>
                        <ActionsMenuSection>
                            <ActionsMenuHeader>Actions</ActionsMenuHeader>
                            <ActionsMenuAction
                                Icon={EnvelopeIcon}
                                menuHidden={false}
                                onClick={handleCompose}>
                                Send email
                            </ActionsMenuAction>
                            <SignWiseMenuAction onClick={handleRequestSignature} />
                            <TransferClientMenuAction onClick={handleTransfer} />
                            <ActionsMenuAction
                                Icon={PaperAirplaneIcon}
                                menuHidden={!canSendToWellSky}
                                onClick={() =>
                                    setSendingToPartner("wellSkyPersonalCare")
                                }>
                                Send to WellSky Personal Care
                            </ActionsMenuAction>
                            <ActionsMenuAction
                                Icon={PaperAirplaneIcon}
                                menuHidden={!canSendToAxisCare}
                                onClick={() => setSendingToPartner("axisCare")}>
                                Send to AxisCare
                            </ActionsMenuAction>
                            <ActionsMenuAction
                                Icon={TrashIcon}
                                menuHidden={false}
                                onClick={handleDelete}>
                                Delete
                            </ActionsMenuAction>
                        </ActionsMenuSection>
                    </ActionsMenu>
                </div>
            </EntityPageHeader>
            {composing ? (
                <MessageCompose
                    entityId={homeCareLeadId}
                    entityType={AdvancedSearchEntityType.HomeCareLead}
                    onDismiss={handleComposeDismiss}
                />
            ) : null}
            <SignatureRequestPanel
                fileEntityType={FileEntityType.HomeCareLead}
                entityId={homeCareLeadId}
                isOpen={requestingSignature}
                onDismiss={handleCloseSignature}
            />
            <SendToPartnerPanel
                homeCareLeadId={homeCareLeadId}
                integration={sendingToPartner}
                onDismiss={handleCloseSendToPartner}
            />
            {transferring ? (
                <TransferClientPanel
                    subject={{ kind: "homeCareLead", id: homeCareLeadId }}
                    displayName={displayName}
                    location={contactTransferLocation({
                        address1: client.selfContactEntity.address1 ?? null,
                        city: client.selfContactEntity.city ?? null,
                        state: client.selfContactEntity.state ?? null,
                        zip: client.selfContactEntity.zip ?? null,
                    })}
                    onDismiss={handleCloseTransfer}
                />
            ) : null}
            <ClientTransferBanner
                subject={{ kind: "homeCareLead", id: homeCareLeadId }}
            />
            <DuplicateRecordBanner
                recordType="homeCareLead"
                recordId={homeCareLeadId}
                nameKey={client.selfContactEntity.name ?? ""}
            />
            <div className="@container space-y-2 px-4 py-2">
                <div className="flex flex-wrap gap-2 md:gap-3">
                    <div>
                        <HomeCareLeadStatusSelector
                            client={client}
                            onStatusChange={handleStatusChange}
                        />
                    </div>
                    <div>
                        <HomeCareLeadAssignedTo
                            client={client}
                            onAssignedUserChange={handleAssignedUserChange}
                        />
                    </div>
                    <div>
                        <HomeCareLeadActionBarDate
                            label="Estimated start"
                            value={client.estimatedStartDate}
                            onChange={handleEstimatedStartDateChange}
                        />
                    </div>
                    <div>
                        <HomeCareLeadActionBarDate
                            label="Start of care"
                            value={client.actualStartDate}
                            onChange={handleActualStartDateChange}
                            maxDate={client.endOfCareDate}
                        />
                    </div>
                    <div>
                        <HomeCareLeadActionBarDate
                            label="End of care"
                            value={client.endOfCareDate}
                            onChange={handleEndOfCareDateChange}
                            disabled={!client.actualStartDate}
                            disabledReason="Set a Start of Care date first"
                            minDate={client.actualStartDate}
                        />
                    </div>
                    <div className="inline-block lg:hidden">
                        <NgByline entity={bylineEntity} mode="action-bar" />
                    </div>
                </div>
                {client.wellSkyClientUrl ? (
                    <a
                        href={client.wellSkyClientUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700">
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        Open in WellSky Personal Care
                    </a>
                ) : null}
                {client.axisCareClientUrl ? (
                    <a
                        href={client.axisCareClientUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700">
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        Open in AxisCare
                    </a>
                ) : null}
            </div>
            <ImportantNoteBanner entityId={homeCareLeadId} className="px-4 pb-3" />
            <DetailPageLayout>
                <Subnav
                    subpages={subpages}
                    selectedSubpage={subpage}
                    defaultSubpage="overview"
                    navigate={handleNavigate}
                    closeSection={handleCloseSection}
                />
            </DetailPageLayout>
        </div>
    );
};
